import {
  defaultEventCategorySet,
  RoutineActivity,
  RoutineActivityRecurring,
  RoutineActivityRecurringDay,
  RoutineRecurring,
  TimeFormatType,
} from '@demind-inc/core';
import { FirestoreRoutineActivity, translateMinToHHhMMmString } from '../../../../data-access';
import { omit } from 'lodash';
import dayjs, { Dayjs } from 'dayjs';
import { formatTimeFromHHMM } from '../../../../helpers';
import { getRecurringDayFromDate, isCustomRecurring } from '../../common/recurrence.helpers';

export interface RoutinePrefItemState {
  [activityId: string]: FirestoreRoutineActivity;
}

export const defaultRecurring: RoutineActivityRecurring = {
  monday: {
    enabled: true,
  },
  tuesday: {
    enabled: true,
  },
  wednesday: {
    enabled: true,
  },
  thursday: {
    enabled: true,
  },
  friday: {
    enabled: true,
  },
  saturday: {
    enabled: true,
  },
  sunday: {
    enabled: true,
  },
};

export const routinePrefItemReducer = (
  state: RoutinePrefItemState,
  action: {
    type: 'update' | 'initialize' | 'add' | 'delete' | 'check' | 'uncheck';
    activityId?: string;
    payload?: Partial<RoutinePrefItemState[string] | RoutinePrefItemState>;
  }
): RoutinePrefItemState => {
  if (action.type === 'initialize') {
    return action.payload as RoutinePrefItemState;
  }
  if (!action.activityId) {
    return state;
  }
  if (action.type === 'add') {
    return {
      ...state,
      [action.activityId || 'routine_creating']: action.payload,
    } as RoutinePrefItemState;
  }
  if (action.type === 'delete') {
    if (state[action.activityId].default) {
      return state;
    }
    return omit(state, action.activityId);
  }
  if (action.type === 'check') {
    return {
      ...state,
      [action.activityId]: {
        ...state[action.activityId],
        selected: true,
      },
    };
  }
  if (action.type === 'uncheck') {
    return {
      ...state,
      [action.activityId]: {
        ...state[action.activityId],
        selected: false,
      },
    };
  }
  return {
    ...state,
    [action.activityId]: {
      ...state[action.activityId],
      ...action.payload,
    },
  };
};

export const transformDBPrefToRoutine = (
  routineActivities: RoutineActivity[]
): RoutinePrefItemState => {
  return routineActivities
    .filter(({ hidden }) => !hidden)
    .reduce((acc, routine) => {
      const { routineActivityId, ...rest } = routine;
      const previousSelected = (rest as { selected?: boolean }).selected;
      acc[routineActivityId] = {
        ...rest,
        // Default to selected so Plan My Day / scheduler can pass eligible routines.
        // Already-scheduled ones are unchecked by useDailyPrefSchedulerChat.
        selected: typeof previousSelected === 'boolean' ? previousSelected : true,
      };
      return acc;
    }, {} as RoutinePrefItemState);
};

export const transformRoutineToDB = (
  routinePrefItem: RoutinePrefItemState
): Array<RoutineActivity> => {
  return Object.entries(routinePrefItem).map(([routineActivityId, routineActivity]) => ({
    routineActivityId,
    ...routineActivity,
  }));
};

export const transformRoutineForEndpoint = (
  routinePrefItem: RoutinePrefItemState,
  selectedDate?: Dayjs,
  rangeEndDate?: Dayjs
): Array<RoutineActivity> => {
  return Object.entries(routinePrefItem)
    .map(([routineActivityId, routineActivity]) => ({
      routineActivityId,
      ...routineActivity,
    }))
    .filter(({ recurring }) =>
      isRecurringEnabledForDateRange(recurring, selectedDate, rangeEndDate)
    );
};

export const isDateEnabledForRecurring = (
  recurring: RoutineRecurring,
  selectedDate?: Dayjs
) => {
  if (!selectedDate) {
    return true;
  }

  if (isCustomRecurring(recurring)) {
    const normalizedSelectedDate = normalizeFloatingDate(selectedDate);
    if (!Number.isInteger(recurring.interval) || recurring.interval < 1) {
      return false;
    }
    if (!isValidStartDate(recurring.startDate)) {
      return false;
    }

    const recurrenceStartDate = normalizeFloatingDate(recurring.startDate);
    if (normalizedSelectedDate.isBefore(recurrenceStartDate, 'day')) {
      return false;
    }

    const dayName = getRecurringDayFromDate(normalizedSelectedDate);
    if (recurring.type === 'weekly') {
      if (!recurring.daysOfWeek.includes(dayName)) {
        return false;
      }

      const weeksFromStart = getWeekStartSunday(normalizedSelectedDate).diff(
        getWeekStartSunday(recurrenceStartDate),
        'week'
      );

      return weeksFromStart >= 0 && weeksFromStart % recurring.interval === 0;
    }
    if (recurring.type === 'monthly-date') {
      const monthsFromStart = getMonthDifference(recurrenceStartDate, normalizedSelectedDate);
      if (monthsFromStart < 0 || monthsFromStart % recurring.interval !== 0) {
        return false;
      }

      const validDays = recurring.daysOfMonth.filter((day) => Number.isInteger(day) && day >= 1 && day <= 31);
      return validDays.includes(normalizedSelectedDate.date());
    }
    if (recurring.type === 'monthly-weekday') {
      const monthsFromStart = getMonthDifference(recurrenceStartDate, normalizedSelectedDate);
      if (monthsFromStart < 0 || monthsFromStart % recurring.interval !== 0) {
        return false;
      }

      if (dayName !== recurring.dayOfWeek) return false;
      if (![-1, 1, 2, 3, 4].includes(recurring.weekOfMonth)) {
        return false;
      }
      if (recurring.weekOfMonth === -1) {
        return isLastWeekdayOfMonth(normalizedSelectedDate);
      }
      return getWeekdayOccurrenceOrdinal(normalizedSelectedDate) === recurring.weekOfMonth;
    }
    return false;
  }

  return recurring[getRecurringDayFromDate(selectedDate)]?.enabled ?? false;
};

export const isRecurringEnabledForDateRange = (
  recurring: RoutineRecurring,
  startDate?: Dayjs,
  endDate?: Dayjs
) => {
  if (!startDate) {
    return true;
  }

  const normalizedStartDate = startDate.startOf('day');
  const normalizedEndDate = endDate?.isBefore(normalizedStartDate, 'day')
    ? normalizedStartDate
    : endDate?.startOf('day');

  if (!normalizedEndDate) {
    return isDateEnabledForRecurring(recurring, normalizedStartDate);
  }

  let cursor = normalizedStartDate;
  while (cursor.isSameOrBefore(normalizedEndDate, 'day')) {
    if (isDateEnabledForRecurring(recurring, cursor)) {
      return true;
    }
    cursor = cursor.add(1, 'day');
  }

  return false;
};

export const translateRoutinePrefToTime = (
  pref: FirestoreRoutineActivity,
  timeFormat: TimeFormatType
) => {
  let value = '';
  let smallValue = '';
  if (pref.startTime && pref.endTime) {
    value = `${formatTimeFromHHMM(pref.startTime, timeFormat)} - ${formatTimeFromHHMM(
      pref.endTime,
      timeFormat
    )}`;
  }
  if (pref.durationMin) {
    smallValue = `(${translateMinToHHhMMmString(pref.durationMin)})`;
  }
  return { value, smallValue };
};

export const initialRoutinePrefItem: RoutinePrefItemState = {
  default_breakfast: {
    name: 'Breakfast',
    startTime: '08:00',
    endTime: '09:00',
    durationMin: 30,
    category: defaultEventCategorySet.find(({ id }) => id === 'default_meal'),
    default: true,
    hidden: false,
    recurring: defaultRecurring,
  },
  default_lunch: {
    name: 'Lunch',
    startTime: '12:00',
    endTime: '13:00',
    durationMin: 30,
    category: defaultEventCategorySet.find(({ id }) => id === 'default_meal'),
    default: true,
    hidden: false,
    recurring: defaultRecurring,
  },
  default_workout: {
    name: 'Workout',
    startTime: '18:00',
    endTime: '19:00',
    durationMin: 60,
    default: true,
    hidden: false,
    category: defaultEventCategorySet.find(({ id }) => id === 'default_exercise'),
    recurring: defaultRecurring,
  },
  default_dinner: {
    name: 'Dinner',
    startTime: '18:00',
    endTime: '20:00',
    durationMin: 60,
    default: true,
    hidden: false,
    category: defaultEventCategorySet.find(({ id }) => id === 'default_meal'),
    recurring: defaultRecurring,
  },
};

const isValidStartDate = (value?: string) =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value) && dayjs(value).format('YYYY-MM-DD') === value;

const normalizeFloatingDate = (value: Dayjs | string) =>
  dayjs(typeof value === 'string' ? value : value.format('YYYY-MM-DD')).startOf('day');

const getWeekStartSunday = (value: Dayjs) => value.startOf('day').subtract(value.day(), 'day');

const getMonthDifference = (startDate: Dayjs, targetDate: Dayjs) =>
  targetDate.year() * 12 + targetDate.month() - (startDate.year() * 12 + startDate.month());

const getWeekdayOccurrenceOrdinal = (targetDate: Dayjs) =>
  Math.floor((targetDate.date() - 1) / 7) + 1;

const isLastWeekdayOfMonth = (targetDate: Dayjs) =>
  targetDate.add(7, 'day').month() !== targetDate.month();
